'use client';
// Top-level Alpha Zone surface. Tab structure mirrors /portfolio exactly
// (Overview / Scorecard / Earnings / Risk) — Steve directive 2026-05-07
// "i feel the alpha zone should have all these pages as well with everything
// thats in them. then both are completely the same".
//
// Each tab pulls from alpha-basket-scoped data:
//   - Overview: alpha-basket.json + alpha-basket-snapshot.json
//   - Scorecard: alpha-basket.json (slot symbols) + scores.json
//   - Earnings:  alpha-upcoming-earnings.json + alpha-earnings-impact.json
//   - Risk:      alpha-basket-snapshot.json
//
// All scoped data files are generated by the cron with `--source=alpha-basket`
// flags on the same compute scripts that produce the portfolio variants.

import { useEffect, useRef, useState } from 'react';
import { AppNav } from '@/components/AppNav';
import { PageTabs, useUrlTab } from '@/components/PageTabs';
import { DATA_SURFACES } from '@/lib/data-surfaces';
import { useResource } from '@/lib/use-resource';
import { AlphaZoneVisualizer } from '@/components/research/AlphaZoneVisualizer';
import { AlphaZoneTracker } from '@/components/research/AlphaZoneTracker';
import { AlphaZoneAttribution } from '@/components/research/AlphaZoneAttribution';
import { AlphaZoneNext10 } from '@/components/research/AlphaZoneNext10';
import { AlphaZoneChanges } from '@/components/research/AlphaZoneChanges';
import { ZonePulse } from '@/components/research/ZonePulse';
import { RecommendedPortfolio } from '@/components/research/RecommendedPortfolio';
import { PortfolioAnalytics } from '@/components/research/PortfolioAnalytics';
import { SectorPie } from '@/components/research/SectorPie';
import { SectorVsBenchmark } from '@/components/research/SectorVsBenchmark';
import { PortfolioScorecard } from '@/components/research/PortfolioScorecard';
import { BASKET_SIZE } from '@/lib/alpha-basket';
import { HoldingsHistory } from '@/components/research/HoldingsHistory';
import { UpcomingEarnings } from '@/components/research/UpcomingEarnings';
import { EarningsImpactTable } from '@/components/research/EarningsImpactTable';
import SectorLeaders from '@/components/research/SectorLeaders';
import { AmBriefCard } from '@/components/research/AmBriefCard';
import { DrawdownChart } from '@/components/research/DrawdownChart';
import { CorrelationHeatmap } from '@/components/research/CorrelationHeatmap';
import { RebalanceSimulator } from '@/components/research/RebalanceSimulator';
import { ScoredNewsFeed } from '@/components/research/ScoredNewsFeed';

const ALPHA_SNAP = '/api/alpha-basket-snapshot';
const ALPHA_TABS = DATA_SURFACES.filter(s => s.href === '/alpha-zone' && s.kind === 'tab');

// /api/alpha-basket envelope: { basket: { slots: [...] }, aggregates, events }
interface BasketResp {
  basket?: { slots?: Array<{ current: { symbol: string } }> };
  /** Filtered to BUY+ (composite ≥ 1.5). Hides slots that have decayed to
   *  HOLD tier even though they remain in the basket file for honest
   *  monthly-rebalance accounting. See /api/alpha-basket. */
  displaySlots?: Array<{ current: { symbol: string } }>;
}

export default function AlphaZonePage() {
  const [tab, setTab] = useUrlTab(ALPHA_TABS, 'overview');
  const activeSurface = ALPHA_TABS.find(s => s.id === tab) ?? ALPHA_TABS[0];

  // Local toggle on the Overview holdings section: the 10 held names vs the
  // Next 10 on deck. View only — does not change BASKET_SIZE. Steve 2026-06-03.
  const [holdingsView, setHoldingsView] = useState<'alpha' | 'next10'>('alpha');

  // Pull the basket symbols once at page level; pass to PortfolioScorecard so
  // the Scorecard tab renders the alpha-basket names instead of falling back
  // to /api/portfolio's 15 holdings.
  const { data: basket } = useResource<BasketResp>('/api/alpha-basket');
  // Prefer displaySlots (BUY+ filtered, ≥ 1.5 composite). Falls back to the
  // full roster if the API is on an older version that hasn't published the
  // filtered field yet.
  const basketSymbols = (basket?.displaySlots ?? basket?.basket?.slots ?? []).map(s => s.current.symbol);

  // Scroll to top on tab change so a long Overview doesn't keep its scroll
  // position when the user clicks Scorecard / Earnings / Risk. Tracking the
  // previous tab in a ref (not state) avoids the setState-in-effect anti-
  // pattern: we only need the comparison value, not a re-render trigger.
  const prevTabRef = useRef(tab);
  useEffect(() => {
    if (tab !== prevTabRef.current) {
      window.scrollTo({ top: 0, behavior: 'instant' });
      prevTabRef.current = tab;
    }
  }, [tab]);

  return (
    <div className="min-h-screen text-[#e2e8f0] pb-12">
      <AppNav />
      <div className="max-w-6xl mx-auto px-4 pt-2">
        <div className="sticky top-[36px] z-40 bg-[#0a0e14] -mx-4 px-4 pt-1 pb-2 border-b border-[#1e293b] shadow-[0_4px_8px_-4px_rgba(0,0,0,0.4)]">
        <h1 className="text-xl font-semibold mb-0.5">Alpha Zone</h1>
        <p className="text-[11px] text-[#64748b] mb-2">
          Top-{BASKET_SIZE} model basket vs SPY · monthly rebalanced · conviction-weighted (k=5, max 15%/name)
        </p>

        <PageTabs surfaces={ALPHA_TABS} active={tab} onChange={setTab} />

        {activeSurface && (
          <p className="text-[11px] text-[#64748b] mb-2 italic">{activeSurface.label}</p>
        )}
        </div>
        <div className="mb-4" />

        {tab === 'overview' && (
          <div className="space-y-6">
            {/* Section 1: Description — long-form prose only on Overview. */}
            <p className="text-[13px] text-[#94a3b8] leading-relaxed max-w-4xl">
              The Alpha Zone holds the top {BASKET_SIZE} names by composite score from a 1,099-stock
              US + Canadian universe — composite blends 40 quality factors across four
              pillars (profitability 45% / growth 20% / shareholder returns 20% /
              safety 15%) with 14 valuation factors (11 peer-relative multiples + 3
              universe-scaled absolute reads), gated to BUY tier (composite ≥ 1.5) so
              only high-conviction names qualify. Position weights tilt toward
              conviction (weight ∝ composite⁵, capped at 15% per name) so the strongest
              ideas get the most capital without any single position dominating the
              basket. The basket rebalances monthly: names that fall out of the top 10
              close at the month-end print and are paired-in by composite rank with the
              new entrants. The conviction tilt was selected over equal-weighting in
              a 12-vintage walk-forward backtest (2014–2025): production rules
              deliver a survivorship-corrected Sharpe of 1.31 at 15.7% annual
              vol, validated against the full Russell 3000 + S&amp;P/TSX Composite.
            </p>

            {/* Section 2: Headline performance card — slot=headline only. */}
            <AlphaZoneTracker slot="headline" />

            {/* Section 3: 3D Q×V scatter scoped to the basket. */}
            <AlphaZoneVisualizer />

            {/* Section 4: Per-position holdings with a local tab toggle —
                "Alpha Zone" (the 10 held names) vs "Next 10" (on deck, ranks
                11-20). View only; the basket stays at BASKET_SIZE=10.
                Steve 2026-06-03. */}
            <div>
              <div className="flex gap-1 mb-3">
                {([
                  { id: 'alpha' as const, label: 'Alpha Zone' },
                  { id: 'next10' as const, label: 'Next 10' },
                ]).map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setHoldingsView(t.id)}
                    className={`px-3 py-1.5 text-[12px] font-semibold rounded-md border transition-colors ${
                      holdingsView === t.id
                        ? 'bg-[#1e3050] border-[#2e4a73] text-white'
                        : 'bg-transparent border-[#1e293b] text-[#64748b] hover:text-[#cbd5e1]'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              {holdingsView === 'alpha'
                ? <AlphaZoneTracker slot="holdings" />
                : <AlphaZoneNext10 onlyNext />}
            </div>

            {/* Zone Pulse — daily composite-driven entries/exits with "why" explanation:
                Q vs V driver, top Q pillar, displacement counterparty. Steve 2026-06-10. */}
            <ZonePulse />

            {/* Material News — Claude-scored sentiment + materiality + taxonomy
                filtered to the alpha-zone basket. Steve directive 2026-05-23. */}
            <ScoredNewsFeed scope="alpha-zone" title="Material News · Alpha Zone" />

            {/* Section 5: Visualizer-sourced vs pre-existing attribution.
                Same component on /portfolio + /alpha-zone. */}
            <AlphaZoneAttribution />

            {/* Analytics + sector mix. Snapshot retired 2026-05-09 — its
                fields lived elsewhere (composite in HoldingsList totals,
                top sector in SectorPie, holdings count in headline). */}
            <PortfolioAnalytics dataUrl={ALPHA_SNAP} title="Alpha Basket Analytics" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <SectorPie dataUrl={ALPHA_SNAP} />
              <SectorVsBenchmark dataUrl={ALPHA_SNAP} />
            </div>

            {/* Alpha-zone-specific extras (no analog on /portfolio). */}
            <AlphaZoneChanges />
            <RecommendedPortfolio />
          </div>
        )}

        {tab === 'scorecard' && (
          <PortfolioScorecard symbols={basketSymbols} title="Alpha Basket — Scored" />
        )}

        {tab === 'history' && (
          <HoldingsHistory symbols={basketSymbols} title="Alpha Basket — Composite History" />
        )}

        {tab === 'earnings' && (
          <div className="space-y-6">
            <UpcomingEarnings dataUrl="/api/upcoming-earnings?source=alpha-basket" />
            {/* EpsRevisionChart removed 2026-05-22 — per-basket chart now
                lives in the StockStoryDrawer; no need for a basket-wide
                grid duplicate here. */}
            <EarningsImpactTable dataUrl="/api/earnings-impact?source=alpha-basket" />
            <div className="border border-[#1e293b] rounded-lg bg-[#0d1117]/50 p-4">
              <div className="text-[10px] uppercase tracking-wider text-[#94a3b8] mb-2 font-semibold">Morning Brief</div>
              <AmBriefCard />
            </div>
          </div>
        )}

        {tab === 'risk' && (
          <div className="space-y-6">
            <DrawdownChart dataUrl={ALPHA_SNAP} />
            <CorrelationHeatmap dataUrl={ALPHA_SNAP} />
            <PortfolioAnalytics dataUrl={ALPHA_SNAP} mode="risk" title="Alpha Basket Risk" />
            <RebalanceSimulator dataUrl={ALPHA_SNAP} />
          </div>
        )}

        {tab === 'leaders' && (
          <SectorLeaders />
        )}
      </div>
    </div>
  );
}
